import * as React from 'react'
import { cn } from '../../lib/cn'
import { CodeBlock } from './CodeBlock'
import { Tabs, TabsContent, TabsList, TabsTrigger } from './TabBar'

/**
 * MarkdocCodeExample — multi-language code sample rendered as tabs.
 *
 * Maps to the Markdoc `{% code-example %}{% example lang="curl" %}…{% /example %}{% example lang="js" %}…{% /example %}{% /code-example %}`
 * tag. v1 supports any languages our regex syntax highlighter (lib/syntax.ts)
 * knows; unknown languages render as plain monospace.
 *
 * Single example without tabs: pass an array of length 1; the tab bar is
 * suppressed and only the CodeBlock renders.
 */
export interface MarkdocCodeExampleEntry {
  language: string
  code: string
  /** Override label shown in the tab. Defaults to the language. */
  label?: string
}

export interface MarkdocCodeExampleProps extends React.HTMLAttributes<HTMLDivElement> {
  examples: MarkdocCodeExampleEntry[]
  /** Index of the initially-selected tab. Default 0. */
  defaultIndex?: number
}

export const MarkdocCodeExample = React.forwardRef<HTMLDivElement, MarkdocCodeExampleProps>(
  ({ className, examples, defaultIndex = 0, ...props }, ref) => {
    if (examples.length === 0) return null
    if (examples.length === 1) {
      const only = examples[0]
      return (
        <div ref={ref} className={className} {...props}>
          <CodeBlock language={only.language} code={only.code} />
        </div>
      )
    }
    const initial = examples[Math.min(Math.max(0, defaultIndex), examples.length - 1)]
    return (
      <div ref={ref} className={cn('flex flex-col', className)} {...props}>
        <Tabs defaultValue={initial.language}>
          <TabsList>
            {examples.map((ex) => (
              <TabsTrigger key={ex.language} value={ex.language}>
                {ex.label ?? ex.language}
              </TabsTrigger>
            ))}
          </TabsList>
          {examples.map((ex) => (
            <TabsContent key={ex.language} value={ex.language}>
              <CodeBlock language={ex.language} code={ex.code} />
            </TabsContent>
          ))}
        </Tabs>
      </div>
    )
  },
)
MarkdocCodeExample.displayName = 'MarkdocCodeExample'
